import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FamlaServices from '../../../services/api/FamlaServices';
import { useAppSelector } from '../../../stores';
import iIndustry from '../../../components/type/iIndustry';

interface Campaign {
    _id: string
    name: string
    description: string
    objective: string
    status: string
    created_at: string
    updated_at: string
    creator_id: string
    company_id: string
    industrie_id?: string
    process_ids?: string[]
}

interface CreateCampaignModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (campaign: Campaign) => void;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const language = localStorage.getItem('currentLanguage') === "fr" ? "french" : "english";
    
    const [formData, setFormData] = useState({
        name: '',
        industrie_id: ''
    });
    
    const [industries, setIndustries] = useState<iIndustry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch industries on mount
    useEffect(() => {
        if (isOpen) {
            fetchIndustries();
        }
    }, [isOpen, language]);

    const fetchIndustries = async () => {
        try {
            setIsLoading(true);
            const data = await FamlaServices("api_host", `/industry/?lang=${language}`, "GET", "", users.access_token);

            console.log("data.body.data", data.body.data);
            
            if (data.status === 200) {
                setIndustries(data.body.data || []);
            }
        } catch (err) {
            console.error('Error fetching industries:', err);
            setError('Failed to load industries');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null); // Clear error on input change
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!formData.name.trim()) {
            setError(t('campaigns.nameRequired'));
            return;
        }
        if (!formData.industrie_id) {
            setError('Industry is required');
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await FamlaServices(
                "api_host",
                "/campaign/",
                "POST",
                {
                    name: formData.name.trim(),
                    industrie_id: formData.industrie_id
                },
                users.access_token
            );

            if (response.status === 201) {
                // Reset form
                setFormData({
                    name: '',
                    industrie_id: ''
                });
                onSuccess(response.body.data);
                onClose();
            } else {
                setError(response.body?.description || 'Failed to create campaign');
            }
        } catch (err: any) {
            console.error('Error creating campaign:', err);
            setError(err.message || 'An error occurred while creating the campaign');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData({
                name: '',
                industrie_id: ''
            });
            setError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    // Sort industries: "Other" at the end
    const sortedIndustries = [...industries].sort((a, b) => {
        const isAOther = ['autre', 'other'].includes(a.name.toLowerCase());
        const isBOther = ['autre', 'other'].includes(b.name.toLowerCase());
        
        if (isAOther && !isBOther) return 1;
        if (!isAOther && isBOther) return -1;
        return a.name.localeCompare(b.name);
    });

    return (
        <div 
            className="modal fade show" 
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content bg-dark border-0">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">
                            {t('campaigns.createNewCampaign')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                            disabled={isSubmitting}
                            aria-label="Close"
                        ></button>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="industrie_id" className="form-label text-light">
                                    {t('campaigns.industry')} <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="industrie_id"
                                    name="industrie_id"
                                    className="form-select bg-dark text-light border-secondary"
                                    value={formData.industrie_id}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isLoading || isSubmitting}
                                >
                                    <option value="">
                                        {isLoading 
                                            ? (t('words.loading')) 
                                            : (t('campaigns.selectIndustry'))
                                        }
                                    </option>
                                    {sortedIndustries.map((industry) => (
                                        <option key={industry._id} value={industry._id}>
                                            {industry.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-3 d-flex align-items-center gap-3">
                                <label htmlFor="name" className="form-label text-light mb-0" style={{ minWidth: '100px' }}>
                                    {t('campaigns.name')} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className="form-control bg-dark text-light border-secondary flex-grow-1"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder={t('campaigns.namePlaceholder')}
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="modal-footer border-0">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                {t('words.close')}
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting || isLoading}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {t('campaigns.creating')}
                                    </>
                                ) : (
                                    t('campaigns.createCampaign')
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateCampaignModal;

